/**
 * Storage Seguro - Implementa criptografia básica para dados sensíveis
 */

import type { UserData } from '../types/user-unified';

interface StorageItem {
  value: string;
  timestamp: number;
  expires?: number;
}

// Interface for debug information
interface DebugInfo {
  value: string | null;
  timestamp?: number;
  expires?: number;
  valid: boolean;
}

class SecureStorage {
  private prefix = 'appagenda_';
  private cryptoKeyPromise: Promise<CryptoKey> | null = null;

  // Gera ou recupera chave de criptografia usando Web Crypto API
  private async getCryptoKey(): Promise<CryptoKey> {
    if (this.cryptoKeyPromise) {
      return this.cryptoKeyPromise;
    }

    this.cryptoKeyPromise = (async () => {
      const storedKeyData = localStorage.getItem('appagenda_crypto_key');

      if (storedKeyData) {
        try {
          const keyData = JSON.parse(atob(storedKeyData));
          return await crypto.subtle.importKey(
            'jwk',
            keyData,
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
          );
        } catch {
          // Se falhar ao importar, gera nova chave
        }
      }

      // Gera nova chave AES-256
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );

      // Salva a chave para reutilização
      const exportedKey = await crypto.subtle.exportKey('jwk', key);
      localStorage.setItem('appagenda_crypto_key', btoa(JSON.stringify(exportedKey)));

      return key;
    })();

    return this.cryptoKeyPromise;
  }

  // Criptografia AES-GCM (muito mais segura que XOR)
  private async encryptData(text: string): Promise<string> {
    try {
      const key = await this.getCryptoKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(text);

      // Gera IV aleatório para cada criptografia
      const iv = crypto.getRandomValues(new Uint8Array(12));

      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        data
      );

      // Combina IV + dados criptografados
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Converte para base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption failed - cannot store data insecurely:', error);
      throw new Error('SecureStorage: encryption unavailable. Ensure the app is running in a secure context (HTTPS).');
    }
  }

  private async decryptData(encryptedText: string): Promise<string> {
    try {
      const key = await this.getCryptoKey();

      // Decodifica de base64
      const combined = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0));

      // Separa IV e dados
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        data
      );

      const decoder = new TextDecoder();
      return decoder.decode(decryptedData);
    } catch (error) {
      console.warn('Decryption failed, trying plain decode:', error);
      try {
        return atob(encryptedText); // Fallback para base64 simples
      } catch {
        return encryptedText;
      }
    }
  }

  private isValidItem(item: StorageItem): boolean {
    if (!item || !item.value || !item.timestamp) {
      return false;
    }

    // Verifica se não expirou
    // IMPORTANTE: Apenas verificar se JÁ EXPIROU, não usar margem de segurança
    // que causa logout prematuro em refreshes
    if (item.expires) {
      const timeUntilExpiry = item.expires - Date.now();
      // Apenas considerar expirado se o tempo já passou
      if (timeUntilExpiry <= 0) {
        return false;
      }
    }

    return true;
  }

  private async getStorageItem(key: string): Promise<StorageItem | null> {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;

      const decrypted = await this.decryptData(item);
      return JSON.parse(decrypted);
    } catch (error) {
      console.warn('Failed to get storage item:', error);
      return null;
    }
  }

  private async setStorageItem(key: string, value: string, expires?: number): Promise<void> {
    try {
      const item: StorageItem = {
        value,
        timestamp: Date.now(),
        expires
      };

      const encrypted = await this.encryptData(JSON.stringify(item));
      localStorage.setItem(this.prefix + key, encrypted);
    } catch (error) {
      console.warn('Failed to set storage item:', error);
    }
  }

  private removeStorageItem(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.warn('Failed to remove storage item:', error);
    }
  }

  // Métodos Públicos (agora assíncronos para suportar Web Crypto API)
  async set(key: string, value: string, expiresInHours?: number): Promise<void> {
    const expires = expiresInHours ? Date.now() + (expiresInHours * 60 * 60 * 1000) : undefined;
    await this.setStorageItem(key, value, expires);
  }

  async get(key: string): Promise<string | null> {
    const item = await this.getStorageItem(key);
    return item && this.isValidItem(item) ? item.value : null;
  }

  remove(key: string): void {
    this.removeStorageItem(key);
  }

  // Métodos específicos para autenticação
  async setToken(token: string, expiresInHours: number = 24): Promise<void> {
    await this.set('auth_token', token, expiresInHours);
  }

  async getToken(): Promise<string | null> {
    return await this.get('auth_token');
  }

  // Verifica se o token ainda é válido (não expirou)
  async isTokenValid(): Promise<boolean> {
    const item = await this.getStorageItem('auth_token');
    return item ? this.isValidItem(item) : false;
  }

  // Verifica se o token precisa de refresh (está próximo de expirar)
  async shouldRefreshToken(): Promise<boolean> {
    const item = await this.getStorageItem('auth_token');
    if (!item || !item.expires) return false;

    const timeUntilExpiry = item.expires - Date.now();
    // Refresh se faltam menos de 30 minutos
    return timeUntilExpiry < 30 * 60 * 1000 && timeUntilExpiry > 0;
  }

  removeToken(): void {
    this.remove('auth_token');
  }

  // Refresh token
  async setRefreshToken(token: string, expiresInHours: number = 168): Promise<void> { // 7 dias
    await this.set('refresh_token', token, expiresInHours);
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.get('refresh_token');
  }

  removeRefreshToken(): void {
    this.remove('refresh_token');
  }

  // User data
  async setUserData(userData: UserData, expiresInHours: number = 24): Promise<void> {
    await this.set('user_data', JSON.stringify(userData), expiresInHours);
  }

  async getUserData(): Promise<UserData | null> {
    const data = await this.get('user_data');
    return data ? JSON.parse(data) as UserData : null;
  }

  removeUserData(): void {
    this.remove('user_data');
  }

  // Limpar todos os dados de autenticação
  clearAuth(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUserData();
  }

  // Verificar se está autenticado
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }

  // Limpar todos os dados (logout completo)
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear storage:', error);
    }
  }

  // Métodos para debugging (apenas desenvolvimento)
  async debugInfo(): Promise<Record<string, DebugInfo>> {
    if (import.meta.env.DEV !== true) {
      return {};
    }

    const info: Record<string, DebugInfo> = {};
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.startsWith(this.prefix)) {
        const cleanKey = key.replace(this.prefix, '');
        const item = await this.getStorageItem(cleanKey);
        info[cleanKey] = {
          value: item?.value ? `${item.value.substring(0, 10)}...` : null,
          timestamp: item?.timestamp,
          expires: item?.expires,
          valid: item ? this.isValidItem(item) : false
        };
      }
    }

    return info;
  }
}

// Exportar instância única
export const secureStorage = new SecureStorage();

// Para compatibilidade com código existente (agora assíncrono)
export default {
  getItem: async (key: string) => await secureStorage.get(key),
  setItem: async (key: string, value: string) => await secureStorage.set(key, value),
  removeItem: (key: string) => secureStorage.remove(key)
};

// Métodos específicos (agora assíncronos para Web Crypto API)
export const authStorage = {
  getToken: async () => await secureStorage.getToken(),
  setToken: async (token: string, expiresInHours?: number) => await secureStorage.setToken(token, expiresInHours),
  removeToken: () => secureStorage.removeToken(),
  isTokenValid: async () => await secureStorage.isTokenValid(),
  shouldRefreshToken: async () => await secureStorage.shouldRefreshToken(),
  getRefreshToken: async () => await secureStorage.getRefreshToken(),
  setRefreshToken: async (token: string, expiresInHours?: number) => await secureStorage.setRefreshToken(token, expiresInHours),
  removeRefreshToken: () => secureStorage.removeRefreshToken(),
  getUserData: async () => await secureStorage.getUserData(),
  setUserData: async (userData: UserData, expiresInHours?: number) => await secureStorage.setUserData(userData, expiresInHours),
  removeUserData: () => secureStorage.removeUserData(),
  clearAuth: () => secureStorage.clearAuth(),
  isAuthenticated: async () => await secureStorage.isAuthenticated(),
  clear: () => secureStorage.clear()
};